// A scanned box: what is in it, what is left and where, then Pull, Move or Put away. Put away
// takes the WHOLE box to a rack section (one field, Enter), so it needs no item picked. Repair
// pulls start from a drone, never here. At the Scrap location the card also offers Thrown out.
// A box in Assembly that knows its section (came_from) opens on Put away with that section named.
// Box empty (manufacturing, shop stock only) writes the whole box down to zero behind a required note.
// It is a quiet key under the card, not a mode on the strip: a write-off is never one tap from Pull.
// When Assembly's pick list wants this box, a primary Pick to Assembly key leads the card; the
// list is fetched in the background and a slow or failed fetch just means no key.
// A finished action hands its sentence to the home, which shows it under the scan field.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePullsApi, type BoxItem, type MoveDestination, type PullReason, type ResolvedBox } from '../../Parts/Pulls/pullsApi';
import { KEY_PRIMARY, KEY_QUIET, KEY_TONAL } from '../Assembly/WorkOrderStation';
import { buzz } from '../shell';
import { canPickStock, canWorkStock } from '../access';
import { serverMessage } from '../errors';
import { useWorkOrdersApi, workOrderErrorMessage } from '../../WorkOrders/workOrdersApi';
import SectionChip from '../../components/SectionChip';
import { FIELD, KeyStrip, Notice, QuantityField, ScreenBar } from '../ui';
import { FOCUS, MOTION, STATE_LAYER } from '../../Requisitions/buttonStyles';
import BoxItemList from './BoxItemList';
import { boxEmptied, itemName, movedTo, pulledForRnd, pulledForScrap, threwOut } from './sentences';

type Mode = 'pull' | 'move' | 'putaway' | 'thrown' | 'empty';

// Where Box empty cannot write off; the server refuses the rest (shop stock only).
// Flashed boards are not refused: they are skipped, as the server skips them.
const NOT_SHOP_STOCK = ['repair', 'scrap'];

interface Props {
  box: ResolvedBox;
  caps: unknown;
  onBack: () => void;
  onFinished: (sentence: string) => void;
}

const matches = (text: string, q: string) => text.toLowerCase().includes(q.trim().toLowerCase());

/** Projects typed or picked: Enter submits the form, the chips only fill the field. */
const ProjectField: React.FC<{
  value: string;
  onChange: (v: string) => void;
  recent: string[];
  inputRef: React.RefObject<HTMLInputElement>;
}> = ({ value, onChange, recent, inputRef }) => {
  const shown = recent.filter((p) => matches(p, value) && p.toLowerCase() !== value.trim().toLowerCase()).slice(0, 4);
  return (
    <div>
      <label htmlFor="scanner-project" className="mb-1 block text-sm text-on-surface-variant">Project</label>
      <input
        id="scanner-project"
        ref={inputRef}
        type="text"
        autoComplete="off"
        enterKeyHint="go"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Type a project or pick one"
        className={FIELD}
      />
      {shown.length > 0 && (
        <div role="group" aria-label="Recent projects" className="mt-2 flex flex-wrap gap-2">
          {shown.map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => { onChange(p); inputRef.current?.focus({ preventScroll: true }); }}
              className={`inline-flex min-h-[2.75rem] max-w-full items-center rounded-full bg-surface-container-highest px-4 text-base text-on-surface ${STATE_LAYER} ${FOCUS} ${MOTION}`}
            >
              <span className="truncate">{p}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const BoxCard: React.FC<Props> = ({ box, caps, onBack, onFinished }) => {
  const api = usePullsApi();
  const woApi = useWorkOrdersApi();
  const canWork = canWorkStock(caps);
  const canPick = canPickStock(caps);
  const { items } = box;

  const usable = (i: BoxItem) => i.pullable || i.movable;
  const [itemId, setItemId] = useState<string | null>(
    items.length === 1 && usable(items[0]) ? items[0].receipt_item_id : null,
  );
  const item = items.find((i) => i.receipt_item_id === itemId) || null;
  const atScrap = item?.location.type === 'scrap';

  const [mode, setMode] = useState<Mode>(box.came_from ? 'putaway' : 'pull');
  // No reason is chosen for the operator: R&D is money against a project, so it is a deliberate tap.
  const [reason, setReason] = useState<PullReason | null>(null);
  const [qty, setQty] = useState('1');
  const [project, setProject] = useState('');
  const [recent, setRecent] = useState<string[]>([]);
  const [destinations, setDestinations] = useState<MoveDestination[] | null>(null);
  const [destinationId, setDestinationId] = useState<number | null>(null);
  const [filter, setFilter] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [section, setSection] = useState('');
  const [note, setNote] = useState('');
  // Lines of Assembly's pick list that include this box. Null until (unless) the list loads.
  const [assemblyNeeds, setAssemblyNeeds] = useState<number | null>(null);
  const projectRef = useRef<HTMLInputElement>(null);

  const remaining = item?.quantity_remaining ?? 0;
  // Scrapping from the Scrap bin means nothing, and a person without operations or
  // manufacturing is refused by the server, so neither is offered.
  const reasons = useMemo(
    () => [
      ...(item?.scrap_only ? [] : [{ key: 'rnd', label: 'R&D' }]),
      ...(canWork && !atScrap ? [{ key: 'scrap', label: 'Scrap' }] : []),
    ],
    [canWork, atScrap, item?.scrap_only],
  );
  const effectiveReason: PullReason | null = reasons.some((r) => r.key === reason) ? reason : null;
  const writeOff = items.filter((i) => i.location.type !== 'flashed');
  const emptyable = writeOff.length > 0 && writeOff.every((i) => !NOT_SHOP_STOCK.includes(i.location.type));
  const modes = useMemo(
    () => [
      { key: 'pull', label: 'Pull' },
      ...(canWork ? [{ key: 'move', label: 'Move' }] : []),
      ...(canWork ? [{ key: 'putaway', label: 'Put away' }] : []),
      ...(canWork && atScrap ? [{ key: 'thrown', label: 'Thrown out' }] : []),
    ],
    [canWork, canPick, atScrap, emptyable],
  );

  useEffect(() => {
    let alive = true;
    api.projects()
      .then((r) => { if (alive) setRecent((r?.projects || []).map((p) => p.project)); })
      .catch(() => { /* typing a project still works without the list */ });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!canPick) return;
    let alive = true;
    Promise.resolve()
      .then(() => woApi.assemblyPickList())
      .then((list) => {
        if (!alive) return;
        const barcode = box.barcode.toLowerCase();
        const line = (list?.lines || []).find(
          (l) => (l.to_move > 0 || l.requests?.length > 0) && l.boxes?.some((b) => b.barcode.toLowerCase() === barcode),
        );
        if (line) setAssemblyNeeds(line.to_move);
      })
      .catch(() => { /* no Pick key without the list; the rest of the card works */ });
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (mode !== 'move' || destinations) return;
    let alive = true;
    api.destinations()
      .then((r) => { if (alive) setDestinations(r?.locations || []); })
      .catch(() => { if (alive) setError('The places you can move this to did not load. Go back and scan the box again.'); });
    return () => { alive = false; };
  }, [mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // An item elsewhere may not offer the action that was open (Thrown out is for the bin only).
  // Box empty is not on the strip: a write-off sits below the card, never one tap from Pull.
  useEffect(() => {
    const open = mode === 'empty' ? canPick && emptyable : modes.some((m) => m.key === mode);
    if (!open) setMode('pull');
  }, [modes, mode, canPick, emptyable]);

  // A changed quantity is a new question: Thrown out confirms again.
  useEffect(() => { setConfirming(false); }, [qty]);

  // A new item or a new action starts from its own default: one for a pull, all for the rest.
  useEffect(() => {
    setQty(mode === 'pull' ? '1' : String(remaining));
    setReason(null);
    setConfirming(false);
    setError(null);
  }, [mode, itemId]); // eslint-disable-line react-hooks/exhaustive-deps

  const refuse = (message: string) => {
    buzz('reject');
    setError(message);
  };

  const quantity = (): number | null => {
    const n = Number(qty);
    if (!Number.isInteger(n) || n < 1) { refuse('Enter how many.'); return null; }
    if (n > remaining) { refuse(`Only ${remaining} left.`); return null; }
    return n;
  };

  const attempt = async (work: () => Promise<string>) => {
    setBusy(true);
    setError(null);
    try {
      onFinished(await work());
    } catch (err: any) {
      setBusy(false);
      // A wedge scan types into the field: leftover text would make the next scan read PLLT-B9PLLT-B3.
      if (mode === 'putaway') setSection('');
      refuse(serverMessage(err, 'That did not go through. Try again.'));
    }
  };

  const pickToAssembly = () => {
    if (busy) return;
    attempt(async () => {
      await woApi.assemblyPick({ barcode: box.barcode });
      return `${box.barcode} is in Assembly.`;
    });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    if (mode === 'putaway') {
      const code = section.trim();
      if (!code) { refuse('Scan or type a section first.'); return; }
      attempt(async () => {
        const r = await api.moveBox(box.barcode, { to_location_code: code });
        return `${box.barcode} is on ${r?.to?.code || r?.to?.name || code.toUpperCase()}.`;
      });
      return;
    }
    if (mode === 'empty') {
      const text = note.trim();
      if (!text) { refuse('Say what happened to the rest, so the count can be checked later.'); return; }
      attempt(async () => {
        try {
          const r = await woApi.assemblyBoxEmpty({ barcode: box.barcode }, text);
          return boxEmptied(box.barcode, r?.zeroed?.length ?? writeOff.length);
        } catch (err) {
          throw new Error(workOrderErrorMessage(err, 'The box did not change. Try again.'));
        }
      });
      return;
    }
    if (!item) return;
    if (mode === 'pull') {
      const q = quantity();
      if (q == null) return;
      if (!effectiveReason) {
        refuse(reasons.length > 1 ? 'Pick R&D or Scrap first.' : `Tap ${reasons[0]?.label ?? 'a reason'} first.`);
        return;
      }
      if (effectiveReason === 'rnd' && !project.trim()) {
        refuse('Name the project this is for.');
        projectRef.current?.focus({ preventScroll: true });
        return;
      }
      const name = project.trim();
      attempt(async () => {
        const r = await api.pull({
          receipt_item_id: item.receipt_item_id,
          quantity: q,
          reason: effectiveReason,
          ...(effectiveReason === 'rnd' ? { project: name } : {}),
        });
        return effectiveReason === 'rnd' ? pulledForRnd(r, name) : pulledForScrap(r);
      });
      return;
    }
    if (mode === 'move') {
      const q = quantity();
      if (q == null) return;
      const to = destinations?.find((d) => d.id === destinationId);
      if (!to) { refuse('Pick where it goes.'); return; }
      attempt(async () => {
        await api.move({ receipt_item_id: item.receipt_item_id, quantity: q, to_location_id: to.id });
        return movedTo(item, q, to.name);
      });
      return;
    }
    const q = quantity();
    if (q == null) return;
    if (!confirming) { setConfirming(true); return; }
    attempt(async () => {
      await api.throwOut([{ receipt_item_id: item.receipt_item_id, quantity: q }]);
      return threwOut(item, q);
    });
  };

  const places = (destinations || []).filter(
    (d) => d.id !== item?.location.id && (matches(d.name, filter) || matches(d.parent_name || '', filter)),
  );
  const onFilterKey = (e: React.KeyboardEvent) => {
    // One match is the answer: Enter takes it. Otherwise Enter falls through and submits.
    if (e.key === 'Enter' && places.length === 1 && destinationId !== places[0].id) {
      e.preventDefault();
      setDestinationId(places[0].id);
    }
  };

  const blocked = item && mode === 'pull' && !item.pullable ? item.refusal : null;
  const submitLabel = (() => {
    const n = Number(qty) > 0 ? qty : '';
    if (mode === 'pull') {
      if (!effectiveReason) return 'Pick a reason';
      return effectiveReason === 'rnd' ? `Pull ${n} for R&D` : `Scrap ${n}`;
    }
    if (mode === 'putaway') return 'Put away';
    if (mode === 'empty') return 'Write off the box';
    if (mode === 'move') {
      const to = destinations?.find((d) => d.id === destinationId);
      return to ? `Move ${n} to ${to.name}` : `Move ${n}`;
    }
    return confirming ? `Yes, throw out ${n}` : `Throw out ${n}`;
  })();

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <ScreenBar title={`Box ${box.barcode}`} onBack={onBack} />
      <div className="flex min-h-0 w-full max-w-md flex-1 flex-col gap-3 self-center overflow-y-auto px-4 pb-6">
        <BoxItemList items={items} selectedId={itemId} usable={usable} onSelect={setItemId} />

        {!item && !canWork ? (
          <p className="px-1 text-base text-on-surface-variant">
            {items.some(usable) ? 'Pick the item you want to work with.' : 'Nothing in this box can be pulled or moved from where it is.'}
          </p>
        ) : (
          <>
            {modes.length > 1 && <KeyStrip label="What to do" options={modes} value={mode} onChange={(k) => setMode(k as Mode)} />}

            {!item && mode !== 'putaway' && mode !== 'empty' ? (
              <p className="px-1 text-base text-on-surface-variant">
                {items.some(usable) ? 'Pick the item you want to work with.' : 'Nothing in this box can be pulled or moved from where it is.'}
              </p>
            ) : (
            <form onSubmit={submit} className="flex flex-col gap-3" noValidate>
              {mode === 'pull' && (
                <>
                  <KeyStrip label="Reason" options={reasons} value={effectiveReason ?? ''} onChange={(k) => setReason(k as PullReason)} />
                  <QuantityField label="Quantity" value={qty} max={remaining} onChange={setQty} />
                  {effectiveReason === 'rnd' && (
                    <ProjectField value={project} onChange={setProject} recent={recent} inputRef={projectRef} />
                  )}
                </>
              )}

              {mode === 'move' && (
                <>
                  <QuantityField label="Quantity" value={qty} max={remaining} onChange={setQty} />
                  <div>
                    <label htmlFor="scanner-destination" className="mb-1 block text-sm text-on-surface-variant">Move to</label>
                    <input
                      id="scanner-destination"
                      type="text"
                      autoComplete="off"
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      onKeyDown={onFilterKey}
                      placeholder="Type to narrow the places"
                      className={FIELD}
                    />
                    {destinations === null ? (
                      <p className="mt-2 px-1 text-sm text-on-surface-variant" role="status">Loading places…</p>
                    ) : places.length === 0 ? (
                      <p className="mt-2 px-1 text-sm text-on-surface-variant">No place matches. Clear the field to see them all.</p>
                    ) : (
                      <ul role="radiogroup" aria-label="Places" className="mt-2 flex max-h-56 flex-col gap-1 overflow-y-auto">
                        {places.map((d) => {
                          const on = d.id === destinationId;
                          return (
                            <li key={d.id}>
                              <button
                                type="button"
                                role="radio"
                                aria-checked={on}
                                onClick={() => setDestinationId(d.id)}
                                className={`flex min-h-[2.75rem] w-full items-baseline gap-2 rounded-lg px-3 py-2 text-left text-base np-state ${FOCUS} ${MOTION}
                                  ${on ? 'bg-surface-container text-on-surface ring-2 ring-primary/50' : 'bg-surface-container-low text-on-surface-variant'}`}
                              >
                                <span className="min-w-0 truncate font-medium text-on-surface">{d.name}</span>
                                {d.parent_name && <span className="min-w-0 truncate text-sm">in {d.parent_name}</span>}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </>
              )}

              {mode === 'putaway' && (
                <div>
                  {box.came_from && (
                    <div className="mb-3 px-1">
                      <p className="flex flex-wrap items-center gap-1.5 text-base text-on-surface">
                        Came from <SectionChip code={box.came_from.code} />
                      </p>
                      <p className="mt-0.5 text-sm text-on-surface-variant">Scan its label to put it back, or scan another section.</p>
                    </div>
                  )}
                  <label htmlFor="scanner-putaway-section" className="mb-1 block text-sm text-on-surface-variant">Section</label>
                  <input
                    id="scanner-putaway-section"
                    type="text"
                    autoComplete="off"
                    autoCapitalize="characters"
                    autoCorrect="off"
                    spellCheck={false}
                    enterKeyHint="go"
                    // The wedge types into a focused field here: this card has no document-level HID listener.
                    autoFocus
                    value={section}
                    onChange={(e) => setSection(e.target.value)}
                    onFocus={(e) => e.currentTarget.select()}
                    placeholder="Scan or type a section"
                    className={`${FIELD} font-mono`}
                  />
                </div>
              )}

              {mode === 'empty' && (
                <>
                  <p className="px-1 text-base text-on-surface-variant">
                    Box empty writes everything below down to zero. It cannot be undone from here.
                  </p>
                  <ul aria-label="What will be written off" className="flex flex-col gap-1">
                    {writeOff.map((i) => (
                      <li key={i.receipt_item_id} className="flex items-baseline gap-2 rounded-lg bg-surface-container-low px-3 py-2 text-base">
                        <span className="font-mono tabular-nums text-on-surface">{i.quantity_remaining}</span>
                        <span className="min-w-0 truncate text-on-surface">{itemName(i)}</span>
                      </li>
                    ))}
                  </ul>
                  <div>
                    <label htmlFor="scanner-empty-note" className="mb-1 block text-sm text-on-surface-variant">What happened to the rest?</label>
                    <textarea
                      id="scanner-empty-note"
                      rows={2}
                      maxLength={500}
                      value={note}
                      onChange={(e) => { setNote(e.target.value); setError(null); }}
                      placeholder="Box was empty on the shelf"
                      className={`${FIELD} resize-y`}
                    />
                  </div>
                </>
              )}

              {mode === 'thrown' && (
                <>
                  <p className="px-1 text-base text-on-surface-variant">
                    Thrown out takes the parts off the books. Put back is not possible.
                  </p>
                  <QuantityField label="Quantity" value={qty} max={remaining} onChange={setQty} />
                </>
              )}

              {blocked && <p className="px-1 text-sm text-red-200">{blocked}</p>}
              {!blocked && mode === 'pull' && item?.hint && <p className="px-1 text-sm text-on-surface-variant">{item.hint}</p>}

              <div className="flex items-center gap-2">
                <button
                  type="submit"
                  disabled={busy || !!blocked}
                  className={mode === 'thrown' || mode === 'empty'
                    ? `${KEY_TONAL} flex-1 !bg-red-900/50 !text-red-100`
                    // Quiet until a reason is tapped: the eye goes to the reason first.
                    : mode === 'pull' && !effectiveReason ? `${KEY_TONAL} flex-1` : `${KEY_PRIMARY} flex-1`}
                >
                  {busy ? 'Working…' : submitLabel}
                </button>
                {confirming && !busy && (
                  <button type="button" onClick={() => setConfirming(false)} className={KEY_QUIET}>Cancel</button>
                )}
              </div>
            </form>
            )}
          </>
        )}

        {error && <div role="alert"><Notice tone="miss">{error}</Notice></div>}

        {canPick && emptyable && mode !== 'empty' && (
          <button type="button" onClick={() => setMode('empty')} className={`${KEY_QUIET} self-start`}>
            Box is empty
          </button>
        )}

        {/* Below the form: the list lands after the card does, and must not move the field under a scan. */}
        {assemblyNeeds !== null && canPick && (
          <div className="flex flex-col gap-2">
            <p className="px-1 text-base text-on-surface">
              {assemblyNeeds > 0 ? `Assembly needs ${assemblyNeeds} of these.` : 'A station asked Assembly for these.'}
            </p>
            <button type="button" onClick={pickToAssembly} disabled={busy} className={KEY_PRIMARY}>
              {busy ? 'Working…' : 'Pick to Assembly'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BoxCard;
